import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    /**
     * When any value in this array changes (shallow comparison), a caught error is cleared and the
     * children are re-rendered. Lets callers reset the boundary without remounting the whole subtree.
     */
    resetKeys?: readonly unknown[];
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error, errorInfo: null };
    }

    componentDidUpdate(prevProps: Props) {
        if (!this.state.hasError) {
            return;
        }

        const prevKeys = prevProps.resetKeys ?? [];
        const nextKeys = this.props.resetKeys ?? [];
        const keysChanged = prevKeys.length !== nextKeys.length || nextKeys.some((key, index) => !Object.is(key, prevKeys[index]));
        if (keysChanged) {
            this.handleReset();
        }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }

        // Update state with error details
        this.setState({
            error,
            errorInfo,
        });

        // TODO: Log to error reporting service (e.g., Sentry, LogRocket)
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return <>{this.props.fallback}</>;
            }

            // Default error UI
            return (
                <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
                    <Card className="w-full max-w-6xl shadow-lg">
                        <CardHeader className="space-y-1 border-b bg-destructive/5 pb-6">
                            <div className="flex items-center gap-3">
                                <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                                    <AlertTriangle className="size-6 text-destructive" />
                                </div>
                                <div>
                                    <CardTitle className="text-xl">Something went wrong</CardTitle>
                                    <CardDescription className="mt-0.5">
                                        We encountered an unexpected error. Please try one of the options below.
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-5 pt-6">
                            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                                <p className="font-medium text-destructive">Error</p>
                                <p className="mt-1 font-mono text-sm text-foreground">{this.state.error?.message || 'Unknown error occurred'}</p>
                                {process.env.NODE_ENV === 'development' && this.state.error?.stack && (
                                    <details className="mt-4 min-w-0 overflow-hidden">
                                        <summary className="cursor-pointer select-none font-medium text-muted-foreground text-sm transition-colors hover:text-foreground">
                                            View stack trace (development only)
                                        </summary>
                                        <pre className="mt-3 max-h-96 min-w-0 max-w-full overflow-auto rounded-md border bg-muted/50 p-3 font-mono text-xs leading-relaxed">
                                            {this.state.error.stack}
                                        </pre>
                                    </details>
                                )}
                            </div>

                            <div className="rounded-lg border bg-muted/30 p-4">
                                <p className="font-medium text-sm">What you can do</p>
                                <ul className="mt-2 space-y-1.5 text-muted-foreground text-sm">
                                    <li className="flex items-center gap-2">
                                        <span className="size-1.5 rounded-full bg-muted-foreground/50" />
                                        Refresh the page to try again
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="size-1.5 rounded-full bg-muted-foreground/50" />
                                        Go back to the home page
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="size-1.5 rounded-full bg-muted-foreground/50" />
                                        Contact support if the problem persists
                                    </li>
                                </ul>
                            </div>
                        </CardContent>
                        <CardFooter className="flex flex-wrap gap-2 border-t bg-muted/20 pt-6">
                            <Button onClick={this.handleReset} variant="outline" size="sm">
                                Try Again
                            </Button>
                            <Button onClick={this.handleReload} variant="outline" size="sm">
                                <RefreshCw className="mr-2 size-4" />
                                Reload Page
                            </Button>
                            <Button onClick={this.handleGoHome} size="sm">
                                <Home className="mr-2 size-4" />
                                Go Home
                            </Button>
                        </CardFooter>
                    </Card>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Hook to reset error boundary from child components
 */
export function useErrorHandler() {
    const [error, setError] = React.useState<Error | null>(null);

    React.useEffect(() => {
        if (error) {
            throw error;
        }
    }, [error]);

    return setError;
}

/**
 * Component-level error boundary wrapper
 */
interface ComponentErrorBoundaryProps {
    children: ReactNode;
    fallback?: (error: Error, reset: () => void) => ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

export function ComponentErrorBoundary({ children, fallback }: ComponentErrorBoundaryProps) {
    return (
        <ErrorBoundary
            fallback={fallback && <ErrorFallback onReset={() => window.location.reload()} error={new Error('Component error')} fallback={fallback} />}
        >
            {children}
        </ErrorBoundary>
    );
}

interface ErrorFallbackProps {
    error: Error;
    onReset: () => void;
    fallback: (error: Error, reset: () => void) => ReactNode;
}

function ErrorFallback({ error, onReset, fallback }: ErrorFallbackProps) {
    return <>{fallback(error, onReset)}</>;
}
